import { Box, Button, List, Row, Text, Title } from '@factorialco/deprecated-design-system'
import { AccountingSettingsPageDocument } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const ItemList = ({ label, route }: { label: string; route: string }) => {
  return (
    <List.Item>
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" width="full">
        <Text.Normal weight="semibold">{label}</Text.Normal>
        <Button.Router to={route} type="secondary" aria-label={label} iconName="arrow-right" />
      </Box>
    </List.Item>
  )
}

const AccountingSettingsPage = () => {
  const { navigation } = useNavigation()

  const { data, loading, error } = useQuery(AccountingSettingsPageDocument, {})

  const canSeeLedgerAccountSettings =
    data?.finance?._policies?.settingsSeeAccountingSettingsLedgerAccounts?.can ?? false
  // VendorsPage should let you assign sale and purchase ledger accounts so we can remove this
  const canManageVendorsPurchaseLedgerAccounts =
    data?.finance?._policies?.journalEntriesManagePurchase?.can ?? false
  const canManagePayrollLedgerAccounts =
    data?.finance?._policies?.journalEntriesManagePurchaseInAccountsPayable?.can ?? false
  const canSeeCustomCategoriesSettings =
    data?.finance?._policies?.settingsSeeAccountingSettingsCustomCategories?.can ?? false
  const canSeeTaxTypesSettings =
    data?.finance?._policies?.settingsSeeAccountingSettingsTaxTypes?.can ?? false

  const canSeeAccountingSettings =
    data?.finance?._policies?.settingsSeeAccountingSettings?.can ?? false

  const accountTypes = [
    ...(canSeeCustomCategoriesSettings
      ? [
          {
            id: 'expense_category',
            label: i18n.t('finance.settings.accounting.custom_categories.label'),
            route: navigation.settings.finance.accounting.expenseCategory.fullPath(),
          },
        ]
      : []),
    ...(canSeeLedgerAccountSettings
      ? [
          {
            id: 'bank_account',
            label: i18n.t('finance.settings.accounting.bank_account.label'),
            route: navigation.settings.finance.accounting.bankAccount.fullPath(),
          },
        ]
      : []),
    ...(canManageVendorsPurchaseLedgerAccounts
      ? [
          {
            id: 'vendor',
            label: i18n.t('finance.settings.accounting.vendor.label'),
            route: navigation.settings.finance.accounting.vendor.fullPath(),
          },
        ]
      : []),
    ...(canManagePayrollLedgerAccounts
      ? [
          {
            id: 'payroll_concept',
            label: i18n.t('finance.settings.accounting.payroll_concept.label'),
            route: navigation.settings.finance.accounting.payrollConcept.fullPath(),
          },
        ]
      : []),
  ]

  return (
    <ResponsivePage.Body
      showHeader={false}
      loading={loading}
      error={error}
      authorized={canSeeAccountingSettings}
    >
      <Box width="full" maxWidth="full" paddingY="s32" gap="s48">
        {accountTypes.length >= 1 && (
          <Box flexDirection="row" paddingY="s16">
            <Row.Col halfWidth>
              <Title
                variant="400"
                title={i18n.t('finance.settings.accounting.title')}
                description={i18n.t('finance.settings.accounting.description')}
                illustration="money-spreadsheet"
              />
            </Row.Col>
            <Row.Col halfWidth>
              <List>
                {accountTypes.map(({ id, label, route }) => (
                  <ItemList key={id} label={label} route={route} />
                ))}
              </List>
            </Row.Col>
          </Box>
        )}
        {canSeeTaxTypesSettings && (
          <Box flexDirection="row" alignItems="center">
            <Row.Col halfWidth>
              <Title
                variant="400"
                title={i18n.t('finance.settings.accounting.title_taxes')}
                description={i18n.t('finance.settings.accounting.description_taxes')}
                illustration="coins"
              />
            </Row.Col>
            <Row.Col halfWidth>
              <List>
                <ItemList
                  key="taxes"
                  label={i18n.t('finance.settings.accounting.tax_type.label')}
                  route={navigation.settings.finance.accounting.taxType.fullPath()}
                />
              </List>
            </Row.Col>
          </Box>
        )}
        {canSeeLedgerAccountSettings && (
          <Box flexDirection="row" alignItems="center">
            <Row.Col halfWidth>
              <Title
                variant="400"
                title={i18n.t('finance.settings.accounting.title_default_configuration')}
                description={i18n.t(
                  'finance.settings.accounting.description_default_configuration'
                )}
                illustration="settings"
              />
            </Row.Col>
            <Row.Col halfWidth>
              <List>
                <ItemList
                  key="default_configuration"
                  label={i18n.t('finance.settings.accounting.default_configuration.label')}
                  route={navigation.settings.finance.accounting.defaultConfiguration.fullPath()}
                />
              </List>
            </Row.Col>
          </Box>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default AccountingSettingsPage
